// Iter-207bb — Retroactive VAT backfill panel (shared component).
//
// Used at the top of both /admin/ndf-drive-health and /admin/cabinet-expenses-drive-health
// pages. Re-runs Gemini OCR on legacy MAD expense lines (NDF + Cabinet) that have a
// receipt but no VAT breakdown. Visible to CEO/ADMIN only.
//
// The underlying backend endpoint (`/admin/expenses/backfill-vat`) scans BOTH
// `expense_lines` and `cabinet_expense_lines`, so we render the same component
// in both pages and the eligibility counters reflect the global totals.
import React from "react";
import { Loader2, RefreshCw, Wrench, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function VatBackfillPanel() {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [starting, setStarting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/expenses/backfill-vat/stats");
      setStats(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Re-subscribe only when the running flag flips.
  const running = stats?.last_job?.status === "RUNNING";
  React.useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => { refresh(); }, 4000);
    return () => clearInterval(id);
  }, [running, refresh]);

  const start = async () => {
    if (!stats?.eligible?.total) {
      toast.info("Aucune ligne éligible à rejouer.");
      return;
    }
    if (!window.confirm(
      `Lancer l'extraction TVA rétroactive sur ${stats.eligible.total} ligne(s) ? `
      + `Cela ré-appelle Gemini sur chaque ticket MAD sans TVA — peut prendre plusieurs minutes.`
    )) return;
    setStarting(true);
    try {
      const r = await api.post("/admin/expenses/backfill-vat");
      toast.success(`Batch démarré (job ${r.data.job_id})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du démarrage");
    } finally {
      setStarting(false);
    }
  };

  const job = stats?.last_job;
  const eligible = stats?.eligible || { ndf: 0, cabinet: 0, total: 0 };

  return (
    <section
      className="border border-zinc-200 rounded-md bg-white p-4 space-y-3"
      data-testid="vat-backfill-panel"
    >
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-zinc-700" />
          <h2 className="text-sm font-medium text-zinc-900">Extraction TVA rétroactive (MAD)</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="vat-backfill-refresh"
            className="inline-flex items-center gap-1.5 px-2.5 h-7 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Actualiser
          </button>
          <button
            onClick={start}
            disabled={starting || running || !eligible.total}
            data-testid="vat-backfill-start"
            className="inline-flex items-center gap-1.5 px-3 h-7 bg-zinc-900 hover:bg-zinc-800 text-white text-[11px] font-mono uppercase tracking-wider rounded-md disabled:opacity-50"
          >
            {starting ? <Loader2 size={11} className="animate-spin" /> : <Wrench size={11} />}
            {running ? "En cours…" : `Lancer (${eligible.total})`}
          </button>
        </div>
      </header>

      <p className="text-[11px] text-zinc-600 leading-relaxed">
        {`Ré-applique l'OCR Gemini sur les anciennes lignes MAD (NDF + Cabinet) qui ont un justificatif mais aucun détail TVA (HT/TVA/TTC).
        Les lignes déjà renseignées ne sont jamais écrasées. Le traitement se lance en tâche de fond — vous pouvez fermer cette page.`}
      </p>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 rounded-md border border-zinc-100 bg-zinc-50/60" data-testid="vat-backfill-eligible-ndf">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Éligibles NDF</div>
          <div className="text-lg font-light text-zinc-800 mt-0.5">{eligible.ndf}</div>
        </div>
        <div className="p-2 rounded-md border border-zinc-100 bg-zinc-50/60" data-testid="vat-backfill-eligible-cabinet">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Éligibles Cabinet</div>
          <div className="text-lg font-light text-zinc-800 mt-0.5">{eligible.cabinet}</div>
        </div>
        <div className="p-2 rounded-md border border-blue-100 bg-blue-50/60" data-testid="vat-backfill-eligible-total">
          <div className="text-[10px] font-mono uppercase tracking-wider text-blue-700">Total éligible</div>
          <div className="text-lg font-light text-blue-900 mt-0.5">{eligible.total}</div>
        </div>
      </div>

      {job && (
        <div className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50 text-[11px] space-y-1.5" data-testid="vat-backfill-job">
          <div className="flex items-center justify-between">
            <span className="font-mono text-zinc-700">
              Job <code className="text-zinc-900">{job.job_id}</code>
              {" · "}
              <span className={
                job.status === "DONE" ? "text-emerald-700" :
                job.status === "RUNNING" ? "text-blue-700" :
                job.status === "FAILED" ? "text-red-700" :
                "text-zinc-700"
              }>
                {job.status}
              </span>
            </span>
            <span className="text-zinc-500">{job.started_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Traités" value={job.processed} testid="vat-backfill-stat-processed" />
            <Stat label="Mis à jour" value={job.updated} color="emerald" testid="vat-backfill-stat-updated" />
            <Stat label="Sans reçu" value={job.skipped_no_receipt} color="zinc" />
            <Stat label="OCR KO" value={job.skipped_ocr_failed} color="amber" />
            <Stat label="Sans TVA visible" value={job.skipped_no_vat_in_receipt} color="zinc" />
          </div>
          {job.last_error && (
            <div className="text-red-700 font-mono text-[10px] mt-1 break-all">
              Erreur : {job.last_error}
            </div>
          )}
          {job.eligible_total > 0 && (
            <div className="h-1 bg-zinc-100 rounded overflow-hidden">
              <div
                className={`h-full ${job.status === "FAILED" ? "bg-red-500" : "bg-blue-500"}`}
                style={{ width: `${Math.min(100, Math.round(100 * (job.processed || 0) / job.eligible_total))}%` }}
                data-testid="vat-backfill-progress-bar"
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, color = "zinc", testid }) {
  return (
    <div className="text-[10px] font-mono" data-testid={testid}>
      <div className={`uppercase tracking-wider text-${color}-600`}>{label}</div>
      <div className={`text-sm text-${color}-800 mt-0.5`}>{value ?? 0}</div>
    </div>
  );
}
